import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, output, viewChild } from '@angular/core';
import { auditTime, combineLatest, delay, filter, map, startWith, switchMap, takeUntil, tap, Unsubscribable } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderSpinnerComponent } from '@aston/foundation/components';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { UnsubscriberClass } from '@aston/foundation/classes';
import { LanguageService } from '@aston/foundation/services';
import 'deep-chat'; // must be written like this
import { Router } from '@angular/router';


import { chatCopyButton, chatTrashButton, systemMessage, temporaryText } from '../../functions/utils.ai';
import { AI_CHAT_CHARACTER_LIMIT, AIChatboxService } from '../../services/ai-chatbox.service';
import { AIChatboxSuggestionsService } from '../../services/ai-chatbox-suggestions.service';
import { AIChatboxStylesService } from '../../services/ai-chatbox.styles.service';
import { AIHelpBarComponent } from '../ai-help-bar/ai-help-bar.component';
import { ChatBotResponse } from '../../models/ai-response.model';
import { AI_MESSAGES_INTERCEPTOR } from '../../di';

interface DeepChat {
	clearMessages: CallableFunction
	requestInterceptor: CallableFunction
	responseInterceptor: CallableFunction
	addMessage(message: any, isUpdate?: boolean): void
	submitUserMessage(message: any): void
	htmlClassUtilities: any
	customButtons: any[]
	htmlWrappers: Record<string, string>
	getMessages(): any[]
	_elementRef: Element
}

@Component({
	selector: 'ai-chat-box',
	styles: `
		:host {
			border-left: 1px solid #d5d5d5;
			display: flex;
			height: 99vh;
		}
		deep-chat {
			font-family: "Niramit", sans-serif;
			max-width: 95% !important;
			min-width: 90% !important;
			z-index: 5 !important;
		}
		.top-bar {
			padding: 11px 40px 0 40px;
			flex-direction: row-reverse;
		}
		.top-bar img {
			margin-bottom: -10px;
		}
		.top-bar h3 {
			margin: 20px 0;
		}
		.ai-message-text {
			background-color: none !important;
		}
	`,
	template: `
		<div class="d-flex flex-column w-100">
			<div class="top-bar">
				<img ngSrc="/assets/images/ava.png" [width]="48" [height]="72" />
				<h3 translate="App.AIChat.Title"></h3>
			</div>
			<ai-chat-help-bar class="position-relative" [beta]="beta()" [insight]="insight()" />
			@if (styles.combined$ | async; as s) {
				@if (ais.connect$ | async; as aiConnect) {
					<deep-chat
						#deepChat
						[connect]="aiConnect"
						[history]="history()"
						[ngStyle]="s.chatboxStyle"
						[avatars]="s.avatarsStyle"
						[errorMessages]="s.errors"
						[textInput]="s.textInputProps"
						[messageStyles]="s.messageStyles"
						[customButtons]="allCustomButtons()"
						[htmlWrappers]="htmlWrappers()"
						[remarkable]="{html:true}"
					></deep-chat>
				} @else {
					<loader-spinner />
				}
			} @else {
				<loader-spinner />
			}
		</div>
	`,
	imports: [CommonModule, NgOptimizedImage, TranslateModule, LoaderSpinnerComponent, AIHelpBarComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AIChatBoxComponent extends UnsubscriberClass {
	suggestions = inject(AIChatboxSuggestionsService)
	styles = inject(AIChatboxStylesService)
	interceptor = inject(AI_MESSAGES_INTERCEPTOR)
	i18n = inject(TranslateService)
	ais = inject(AIChatboxService)
	lang = inject(LanguageService)
	router = inject(Router)

	builtinsCustomButtons = [
		{
			...chatTrashButton,
			onClick: () => {
				// Handler to trash the current thread
				this.deepChat().clearMessages();
				this.ais.startNewThread();
				this.trash.emit()
			}
		}
	]

	beta = input(false)
	insight = input(false)
	history = input<ChatBotResponse[]>()
	customButtons = input<any[]>([])

	trash = output<void>()

	deepChatRef = viewChild('deepChat')
	deepChat = computed(() => (this.deepChatRef() as any)?.nativeElement as DeepChat)
	allCustomButtons = computed(() => ([] as any[]).concat(this.builtinsCustomButtons, this.customButtons()))
	htmlWrappers = computed(() => ({
		ai: `
			<div>
				<span class="html-wrapper stream-wrapper"></span>
				<div class="stream-actions" style="text-align:right;">
					<span class="stream-actions-copy custom-button-container-default" title="${this.i18n.instant('Actions.Copy')}">${chatCopyButton.styles.button.default.svg.content}</span>
				</div>
			</div>
		`,
	}))

	handleClick = this.interceptor.handleClick.bind(this.interceptor)

	subscriptions = [] as Unsubscribable[]
	attachEvents = effect(() => {
		if (!this.deepChat()) return;
		this.subscriptions.forEach(s => s.unsubscribe())
		this.subscriptions = []

		this.subscriptions.push(
			this.refreshIntro$.subscribe(),
			this.appendPageSuggestions$.subscribe(),
			this.translateCustomButtonTooltips$.subscribe(),
			this.disableCustomButtonsWhileLoading$.subscribe(),
			this.replaceEntityTokensAfterStream$.subscribe(),
		)

		this.deepChat().htmlClassUtilities = {
			['stream-actions-copy']: {
				events: {
					click: (event: Event) => this.interceptor.handleClipboardCopy(event),
				},
				styles: chatCopyButton.styles.button.default.container,
			},
			['ai-entity-token']: {
				events: {
					click: (event: Event) => this.interceptor.handleEntityClick(event),
				},
			},
			['ai-entity-action']: {
				events: {
					click: (event: Event) => this.interceptor.handleEntityClick(event),
				},
			},
		};

		document.addEventListener('click', (e: Event) => {
			const inToken = (e.composedPath() as HTMLElement[]).some(el => el?.classList?.contains?.('ai-entity-token'))
			if (!inToken) this.interceptor.closeAllEntityMenus(this.deepChat()._elementRef as HTMLElement)
		})

		// Rewrite response and add contextual buttons
		this.deepChat().responseInterceptor = this.hydrateResponse
		setTimeout(() => this.appendInputCharsLimit(), 500);

		// ugly hack for chrome browser lack of originalTarget from shadow-dom
		(window as any).navigateByUrl = this.router.navigateByUrl.bind(this.router);
	})

	disableCustomButtonsWhileLoading$ = this.ais.loading$.pipe(
		map(loading => ({ loading, buttons: this.findCustomButtons() })),
		filter(({buttons}) => !!buttons),
		tap(({loading, buttons}) => buttons.forEach((btn: HTMLElement) => btn.style.opacity = loading ? '0' : '.7')),
	)

	translateCustomButtonTooltips$ = combineLatest([this.ais.loading$, this.lang.language$]).pipe(
		map(_ => this.findCustomButtons('[data-tooltip-translate]')),
		tap(buttons => buttons
			.filter(btn => btn.hasAttribute('data-tooltip-translate'))
			.forEach((btn: HTMLElement) => btn.setAttribute('title', this.i18n.instant(String(btn.getAttribute('data-tooltip-translate')))))
		),
	)

	// Display a welcome message once, and after lang change
	refreshIntro$ = this.lang.language$.pipe(
		startWith(null),
		switchMap(_ => this.ais.intro$),
		auditTime(300),
		tap(messages => messages.forEach(html => this.deepChat().addMessage(systemMessage({ html })))),
		takeUntil(this.destroySubscriptions$)
	)
	
	// After full response arrives, replace entity tokens in the last stream-wrapper
	replaceEntityTokensAfterStream$ = this.ais.onFinalResponse$.pipe(
		delay(50),
		tap(response => {
			if (!response.text) {
				console.warn('[ai-chat] onResponse without text', response)
				return;
			}
			const html = this.interceptor.hydrateFullResponse(response.text)
			if (!html) {
				console.debug('[ai-chat] onResponse without entity tokens, skipping hydration', response)
				return;
			}
			const wrappers = this.deepChat()._elementRef.querySelectorAll('.stream-wrapper')
			const last = wrappers[wrappers.length - 1] as HTMLElement | undefined
			console.debug('[ai-chat] replacing entity tokens in last stream wrapper', { html, response, last })
			if (last) {
				last.innerHTML = html
				last.addEventListener('click', (e: Event) => this.interceptor.handleEntityClick(e), { capture: true })
			}
		}),
		takeUntil(this.destroySubscriptions$),
	)

	// When page suggestions change, display them
	appendPageSuggestions$ = this.suggestions.pageContextSuggestionsButtons$.pipe(
		auditTime(1000),
		filter(buttons => buttons?.length > 0),
		map(buttons => this.deepChat().addMessage(systemMessage({ html: temporaryText(buttons.join('')) }))),
		takeUntil(this.destroySubscriptions$),
	)

	findCustomButtons(selector = '.custom-button') {
		return Array.from(this.deepChat()._elementRef.querySelectorAll(selector)) as HTMLElement[]
	}

	appendInputCharsLimit() {
		const input = this.deepChat()._elementRef.querySelector('#text-input-container')
		if (!input) return;
		const help = document.createElement('div')
		Object.assign(help.style, {
			position: 'absolute',
			opacity: 0.4,
			left: '5px',
			bottom: 0,
		})
		help.innerHTML = this.i18n.instant('Forms.FieldErrorMessages.MaxCharacters', { count: AI_CHAT_CHARACTER_LIMIT });
		input?.appendChild(help)
	}

	// Rewrite response and add contextual buttons
	hydrateResponse = (response: any) => new Promise(resolve => {
		const [replaced, hydrated] = this.interceptor.hydrateResponse(response)
		setTimeout(() => this.deepChat().addMessage(hydrated), 200)
		resolve(replaced)
	})

	getMessages(): ChatBotResponse[] {
		return this.deepChat().getMessages() as ChatBotResponse[]
	}

	addMessage(message: ChatBotResponse) {
		return this.deepChat().addMessage(message)
	}

	submitUserMessage(message: ChatBotResponse) {
		return this.deepChat().submitUserMessage(message)
	}
}
